interface CacheItem {
  data: unknown
  timestamp: number
}

// 默认配置
const DEFAULT_TTL = 1000 * 60 * 60 * 24 * 7 // 7天
class CacheManager {
  private readonly prefix = `${import.meta.env.VITE_BRAND_NAME}_CACHE_`

  constructor() {
    // 开发环境自动清理过期缓存
    // if (process.env.NODE_ENV === 'development') {
    //   this.cleanExpiredCache()
    // }
  }

  /**
   * 核心方法：获取数据（推荐使用）
   * @param key 缓存键
   * @param fetcher 数据获取函数
   * @param ttl 过期时间(毫秒)，默认15分钟
   */
  async get<T = unknown>(
    key: string,
    fetcher: () => Promise<T>,
    ttl: number = DEFAULT_TTL
  ): Promise<T | null> {
    const cacheKey = this.prefix + key

    // 检查缓存
    const cachedData = this.getCache<T>(cacheKey)
    if (cachedData && this.isCacheValid(cacheKey, ttl)) {
      // 有效缓存，异步更新
      console.log(`✅ 缓存命中: ${key}`, {
        hasData: !!cachedData,
        isValid: true
      })
      this.updateAsync(cacheKey, fetcher)
      return cachedData
    }

    // 检查是否有过期缓存
    const hasExpiredCache = !!cachedData
    console.log(`❌ 缓存未命中: ${key}`, {
      hasExpiredCache,
      reason: hasExpiredCache ? '缓存已过期' : '无缓存数据'
    })

    // 无有效缓存，同步获取
    try {
      const data = await fetcher()
      this.setCache(cacheKey, data)
      console.log(`📦 数据已缓存: ${key}`)
      return data
    } catch (error) {
      console.error(`获取数据失败: ${key}`, error)
      return cachedData || null
    }
  }

  /**
   * 强制刷新缓存
   */
  async refresh<T = unknown>(key: string, fetcher: () => Promise<T>): Promise<T | null> {
    const cacheKey = this.prefix + key
    console.log(`🔄 强制刷新缓存: ${key}`)
    try {
      const data = await fetcher()
      this.setCache(cacheKey, data)
      console.log(`✅ 缓存刷新成功: ${key}`)
      return data
    } catch (error) {
      console.error(`刷新缓存失败: ${key}`, error)
      return this.getCache<T>(cacheKey) || null
    }
  }

  /**
   * 检查是否有缓存
   */
  hasCache(key: string): boolean {
    return this.getCache(this.prefix + key) !== null
  }

  /**
   * 清除指定缓存
   */
  clear(key: string): void {
    const cacheKey = this.prefix + key
    const hadCache = !!localStorage.getItem(cacheKey)
    localStorage.removeItem(cacheKey)
    if (hadCache) {
      console.log(`🗑️ 缓存已清除: ${key}`)
    }
  }

  /**
   * 清除所有缓存
   */
  clearAll(): void {
    const keys = Object.keys(localStorage).filter((k) => k.startsWith(this.prefix))
    keys.forEach((key) => localStorage.removeItem(key))
    console.log(`🗑️ 已清除所有缓存，共 ${keys.length} 项`)
  }

  /**
   * 检查缓存是否有效
   */
  private isCacheValid(key: string, ttl: number): boolean {
    try {
      const cacheStr = localStorage.getItem(key)
      if (!cacheStr) return false

      const cache: CacheItem = JSON.parse(cacheStr)
      return Date.now() - cache.timestamp < ttl
    } catch {
      return false
    }
  }

  /**
   * 获取缓存数据
   */
  private getCache<T = unknown>(key: string): T | null {
    try {
      const cacheStr = localStorage.getItem(key)
      if (!cacheStr) return null

      const cache: CacheItem = JSON.parse(cacheStr)
      return cache.data as T
    } catch {
      localStorage.removeItem(key)
      return null
    }
  }

  /**
   * 设置缓存数据
   */
  private setCache(key: string, data: unknown): void {
    try {
      const cache: CacheItem = {
        data,
        timestamp: Date.now()
      }
      localStorage.setItem(key, JSON.stringify(cache))
    } catch (error) {
      console.warn(`设置缓存失败: ${key}`, error)
    }
  }

  /**
   * 异步更新缓存
   */
  private updateAsync<T>(key: string, fetcher: () => Promise<T>): void {
    console.log(`🔄 异步更新缓存: ${key.replace(this.prefix, '')}`)
    fetcher()
      .then((data) => {
        this.setCache(key, data)
        console.log(`✅ 异步更新成功: ${key.replace(this.prefix, '')}`)
      })
      .catch((error) => console.warn(`异步更新缓存失败: ${key.replace(this.prefix, '')}`, error))
  }

  /**
   * 清理过期缓存
   */
  cleanExpiredCache(): number {
    let cleanedCount = 0
    const keys = Object.keys(localStorage).filter((k) => k.startsWith(this.prefix))

    console.log(`🧹 开始清理过期缓存，检查 ${keys.length} 项`)

    keys.forEach((key) => {
      try {
        const cacheStr = localStorage.getItem(key)
        if (cacheStr) {
          const cache: CacheItem = JSON.parse(cacheStr)
          const age = Date.now() - cache.timestamp

          // 清理超过1小时的缓存
          if (age > 1000 * 60 * 60) {
            localStorage.removeItem(key)
            cleanedCount++
          }
        }
      } catch {
        localStorage.removeItem(key)
        cleanedCount++
      }
    })

    console.log(`🧹 清理完成，已清除 ${cleanedCount} 项过期缓存`)
    return cleanedCount
  }
}

// 创建全局实例
export const cacheManager = new CacheManager()

// 便捷函数：创建缓存实例
export const createCache = <T>(key: string, fetcher: () => Promise<T>, ttl?: number) => ({
  get: () => cacheManager.get(key, fetcher, ttl),
  refresh: () => cacheManager.refresh(key, fetcher),
  clear: () => cacheManager.clear(key),
  hasCache: () => cacheManager.hasCache(key)
})

// 导出类型
export { CacheManager }
